<script setup lang="ts">
import { computed, ref } from 'vue'
import { Eye, EyeOff, Search, XCircle } from '@lucide/vue'
import type { ButtonSize } from '../../types'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  value?: string | number | null
  defaultValue?: string | number
  type?: string
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  allowClear?: boolean
  size?: ButtonSize
  rows?: number | string
  maxlength?: number | string
  status?: 'error' | 'warning'
  addonBefore?: string
  addonAfter?: string
}>(), { type: 'text', placeholder: '', disabled: undefined, readonly: false, allowClear: false, rows: 3 })
const emit = defineEmits<{
  'update:value': [value: string]
  input: [event: Event]
  change: [event: Event]
  pressEnter: [event: KeyboardEvent]
  clear: []
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()
const { attrs, disabled: mergedDisabled, size: mergedSize, status: mergedStatus, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const inputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
const innerValue = ref(props.defaultValue ?? '')
const showPassword = ref(false)
const isFocused = ref(false)
const composing = ref(false)
const actualType = computed(() => props.type === 'password' && showPassword.value ? 'text' : props.type)
const mergedValue = useBoundValue(props, 'value', innerValue)
const stringValue = computed(() => String(mergedValue.value ?? ''))
function handleInput(event: Event) {
  if (composing.value || mergedDisabled.value || props.readonly) return
  const value = (event.target as HTMLInputElement).value
  innerValue.value = value
  emit('update:value', value)
  emit('input', event)
  emit('change', event)
  return onFieldChange()
}
function handleCompositionEnd(event: CompositionEvent) {
  if (!composing.value) return
  composing.value = false
  return handleInput(event)
}
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !composing.value && !event.isComposing) emit('pressEnter', event)
}
function handleClear() {
  if (mergedDisabled.value || props.readonly || !inputRef.value) return
  inputRef.value.value = ''
  inputRef.value.dispatchEvent(new Event('input', { bubbles: true }))
  emit('clear')
  inputRef.value.focus()
}
function handleFocus(event: FocusEvent) { isFocused.value = true; emit('focus', event) }
function handleBlur(event: FocusEvent) { isFocused.value = false; emit('blur', event); return onFieldBlur() }
function togglePassword() { showPassword.value = !showPassword.value }
defineExpose({ focus: (options?: FocusOptions) => inputRef.value?.focus(options), blur: () => inputRef.value?.blur(), input: inputRef })
</script>

<template>
  <div
    v-if="type !== 'textarea'"
    class="rr-input-group-wrapper"
    :style="attrs.style as any"
    :class="[
      `rr-input-group--${mergedSize}`, attrs.class,
      {
        'has-addon-before': Boolean($slots.addonBefore || addonBefore),
        'has-addon-after': Boolean($slots.addonAfter || addonAfter)
      }
    ]"
  >
    <!-- Addon Before -->
    <span v-if="$slots.addonBefore || addonBefore" class="rr-input__addon rr-input__addon--before">
      <slot name="addonBefore">{{ addonBefore }}</slot>
    </span>

    <div
      class="rr-input-wrapper"
      :class="[
        `rr-input--${mergedSize}`,
        {
          'is-disabled': mergedDisabled,
          'is-focused': isFocused,
          'is-error': mergedStatus === 'error',
          'is-warning': mergedStatus === 'warning',
          'has-prefix': Boolean($slots.prefix) || type === 'search',
          'has-suffix': Boolean($slots.suffix) || allowClear || type === 'password'
        }
      ]"
    >
      <!-- Prefix -->
      <span v-if="$slots.prefix || type === 'search'" class="rr-input__prefix">
        <slot name="prefix">
          <Search v-if="type === 'search'" :size="mergedSize === 'small' ? 12 : 14" aria-hidden="true" />
        </slot>
      </span>

      <!-- Input Element -->
      <input
        ref="inputRef"
        v-bind="controlAttrs()"
        :value="stringValue"
        :type="actualType"
        :placeholder="placeholder"
        :disabled="mergedDisabled"
        :readonly="readonly"
        :maxlength="maxlength"
        class="rr-input"
        @input="handleInput"
        @compositionstart="composing = true"
        @compositionend="handleCompositionEnd"
        @keydown="handleKeydown"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <!-- Suffix / Clear / Password -->
      <span v-if="$slots.suffix || (allowClear && stringValue && !mergedDisabled && !readonly) || type === 'password'" class="rr-input__suffix">
        <button
          v-if="allowClear && stringValue && !mergedDisabled && !readonly"
          type="button"
          class="rr-input__clear-btn"
          aria-label="清空输入"
          @click.stop="handleClear"
        >
          <XCircle :size="13" aria-hidden="true" />
        </button>

        <button
          v-if="type === 'password' && !mergedDisabled"
          type="button"
          class="rr-input__pwd-toggle"
          :aria-label="showPassword ? '隐藏密码' : '显示密码'"
          @click.stop="togglePassword"
        >
          <EyeOff v-if="showPassword" :size="14" aria-hidden="true" />
          <Eye v-else :size="14" aria-hidden="true" />
        </button>

        <slot name="suffix" />
      </span>
    </div>

    <!-- Addon After -->
    <span v-if="$slots.addonAfter || addonAfter" class="rr-input__addon rr-input__addon--after">
      <slot name="addonAfter">{{ addonAfter }}</slot>
    </span>
  </div>

  <!-- Textarea Element -->
  <div
    v-else
    class="rr-input-wrapper rr-textarea-wrapper"
    :style="attrs.style as any"
    :class="[attrs.class, {
      'is-disabled': mergedDisabled,
      'is-focused': isFocused,
      'is-error': mergedStatus === 'error',
      'is-warning': mergedStatus === 'warning'
    }]"
  >
    <textarea
      ref="inputRef"
        v-bind="controlAttrs()"
      :value="stringValue"
      :placeholder="placeholder"
      :disabled="mergedDisabled"
      :readonly="readonly"
      :rows="rows"
      :maxlength="maxlength"
      class="rr-textarea"
      @input="handleInput"
      @compositionstart="composing = true"
        @compositionend="handleCompositionEnd"
      @keydown="handleKeydown"
      @focus="handleFocus"
      @blur="handleBlur"
    />
  </div>
</template>

<style scoped>
.rr-input-group-wrapper {
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;
  vertical-align: top;
}

.rr-input__addon {
  display: inline-flex;
  align-items: center;
  padding: 0 11px;
  background: var(--rr-color-surface-subtle, #f7f8fa);
  border: 1px solid var(--rr-color-border-strong, #cbd2da);
  color: var(--rr-color-text-muted, #67717f);
  font-size: var(--rr-font-size-base, 13px);
  white-space: nowrap;
}

.rr-input__addon--before {
  border-right: 0;
  border-top-left-radius: var(--rr-radius-control, 6px);
  border-bottom-left-radius: var(--rr-radius-control, 6px);
}

.rr-input__addon--after {
  border-left: 0;
  border-top-right-radius: var(--rr-radius-control, 6px);
  border-bottom-right-radius: var(--rr-radius-control, 6px);
}

.has-addon-before .rr-input-wrapper {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.has-addon-after .rr-input-wrapper {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.rr-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong, #cbd2da);
  border-radius: var(--rr-radius-control, 6px);
  background: var(--rr-color-surface, #ffffff);
  color: var(--rr-color-text, #2b3038);
  font-size: var(--rr-font-size-base, 13px);
  transition: all var(--rr-motion-duration-mid, 200ms) var(--rr-motion-ease-out, ease);
}

.rr-input-wrapper:hover:not(.is-disabled) {
  border-color: var(--rr-color-primary, #5a46e0);
}

.rr-input-wrapper.is-focused:not(.is-disabled) {
  border-color: var(--rr-color-primary, #5a46e0);
  box-shadow: 0 0 0 3px var(--rr-color-primary-soft, #f0edfc);
}

.rr-input-wrapper.is-disabled {
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-text-subtle, #8a94a2);
  cursor: not-allowed;
  opacity: 0.65;
}

.rr-input-wrapper.is-error {
  border-color: var(--rr-color-danger, #be3038);
}
.rr-input-wrapper.is-error.is-focused {
  box-shadow: 0 0 0 3px var(--rr-color-danger-soft, #fff0f1);
}

.rr-input-wrapper.is-warning {
  border-color: var(--rr-color-warning, #946008);
}

/* Sizes */
.rr-input--small {
  height: 24px;
  padding: 0 7px;
  font-size: var(--rr-font-size-xs, 11px);
}
.rr-input--middle {
  height: 32px;
  padding: 0 10px;
  font-size: var(--rr-font-size-base, 13px);
}
.rr-input--large {
  height: 40px;
  padding: 0 14px;
  font-size: var(--rr-font-size-md, 14px);
}

.rr-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: 0;
  box-shadow: none;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  padding: 0;
}

.rr-input::placeholder {
  color: var(--rr-color-text-subtle);
}

.rr-input:disabled {
  cursor: not-allowed;
}

.rr-input__prefix,
.rr-input__suffix {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--rr-color-text-muted);
  flex-shrink: 0;
}

.rr-input__prefix { margin-right: 6px; }
.rr-input__suffix { margin-left: 6px; }

.rr-input__clear-btn,
.rr-input__pwd-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: unset !important;
  min-height: unset !important;
  border: 0 !important;
  background: transparent !important;
  color: var(--rr-color-text-subtle) !important;
  cursor: pointer;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1;
}

.rr-input__clear-btn:hover,
.rr-input__pwd-toggle:hover {
  color: var(--rr-color-text-strong) !important;
}

/* Textarea */
.rr-textarea-wrapper {
  padding: 6px 10px;
}

.rr-textarea {
  width: 100%;
  border: 0;
  outline: 0;
  box-shadow: none;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  line-height: 1.5;
  box-sizing: border-box;
}

.rr-textarea::placeholder {
  color: var(--rr-color-text-subtle);
}
</style>
